import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Skeleton, SvgIcon } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { Card, CardActionArea } from "@mui/material";
import StoryCard from "../components/StoryCard";
import { fetchUserStories } from "../utils/api";
import { fetchUserInteractiveStories } from "../utils/apis/interactiveStoriesAPI";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon } from "@mui/icons-material";
import { t } from "@lingui/macro";

const NewStoryCard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const ICON_SLOT = 84;        // fixed row for icon
  const TEXT_MIN_HEIGHT = 56;  // fixed row for text

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        overflow: "hidden",
        backgroundColor: "transparent",
        transition: "transform 0.1s, box-shadow 0.1s",
        "&:hover": { transform: "scale(1.05)", boxShadow: theme.shadows[8] },
        cursor: "pointer",
        margin: theme.spacing(0),
      }}
      onClick={() => navigate("/mystories/new")}
    >
      <CardActionArea
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateRows: `${ICON_SLOT}px ${TEXT_MIN_HEIGHT}px`,
          alignItems: "center",
          justifyItems: "center",
          color: theme.palette.callToAction.main,
          rowGap: 8,
        }}
      >
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AddIcon sx={{ fontSize: 40, color: theme.palette.callToAction.main }} />
        </div>
        <Typography
          variant="h6"
          align="center"
          sx={{
            color: theme.palette.callToAction.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1.2,
          }}
          ml={1}
          mr={1}
        >
          {t`Write A New Story`}
        </Typography>
      </CardActionArea>
    </Card>
  );
};

const NewInteractiveStoryCard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const ICON_SLOT = 84;
  const TEXT_MIN_HEIGHT = 56;

  const CARD_GRADIENT =
    theme.palette.mode === "dark"
      ? "linear-gradient(140deg, rgba(229,57,53,0.10) 0%, rgba(123,31,162,0.08) 48%, rgba(30,136,229,0.10) 100%)"
      : "linear-gradient(140deg, rgba(229,57,53,0.08) 0%, rgba(123,31,162,0.06) 48%, rgba(30,136,229,0.08) 100%)";

  const TEXT_GRADIENT = "linear-gradient(135deg, #ff8400ff 30%, #dc17c5ff 60%, #7700ffff 90%)";

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        overflow: "hidden",
        background: CARD_GRADIENT,
        transition: "transform 0.1s, box-shadow 0.1s",
        "&:hover": { transform: "scale(1.05)", boxShadow: theme.shadows[8] },
        cursor: "pointer",
        margin: theme.spacing(0),
      }}
      onClick={() => navigate("/mystories/new?type=interactive")}
    >
      <CardActionArea
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          gridTemplateRows: `${ICON_SLOT}px ${TEXT_MIN_HEIGHT}px`,
          alignItems: "center",
          justifyItems: "center",
          color: theme.palette.callToAction.main,
          rowGap: 8,

          position: "relative",
          "--cta": theme.palette.callToAction.main,
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(80% 60% at 50% 40%, var(--cta) 0%, transparent 60%)",
            opacity: 0.12,                       // visible glow by default
            pointerEvents: "none",
            transition: "opacity .2s ease",
            animation: "glowPulse 3.2s ease-in-out infinite",
          },
          "&:hover::after": { opacity: 0.22 },   // a bit brighter on hover
          "@keyframes glowPulse": {
            "0%, 100%": { opacity: 0.12 },
            "50%": { opacity: 0.18 }
          },

          "&::before": {
            content: '""',
            position: "absolute",
            inset: -1,
            background: "linear-gradient(120deg, transparent, rgba(255,255,255,.18), transparent)",
            transform: "translateX(-100%)",
            transition: "transform .8s ease",
            pointerEvents: "none",
          },
          "&:hover::before": { transform: "translateX(100%)" },

        }}
      >
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AddIcon sx={{ fontSize: 40, color: "#ff8400ff" }} />
        </div>
        <Typography
          variant="h6"
          align="center"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1.2,
            background: TEXT_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
          ml={1}
          mr={1}
        >
          {t`New Interactive Story`}
        </Typography>
      </CardActionArea>
    </Card>
  );
};


const SkeletonStoryCard = () => {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "rgba(0,0,0,0.1)",
      }}
    >
      <Skeleton variant="rectangular" width="100%" height="80%" />
      <Box sx={{ padding: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </Box>
    </Card>
  );
};

const MyStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    let cancelled = false;

    // Normalize any timestamp-ish value to a sortable number (seconds since epoch)
    const ts = (v) => {
      if (!v) return 0;

      // plain number (already a timestamp)
      if (typeof v === "number") return v;

      // ISO string
      if (typeof v === "string") {
        const parsed = Date.parse(v);
        return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : 0;
      }

      // Firestore-like objects
      if (typeof v === "object") {
        if (typeof v._seconds === "number") return v._seconds; // CF JSON
        if (typeof v.seconds === "number") return v.seconds;   // client SDK
      }

      return 0;
    };

    // Last-activity logic:
    // - interactive: use updatedAt → publishedAt → createdAt
    // - text/normal: use max(chapter.updatedAt, story.updatedAt, story.createdAt)
    const latestStoryActivity = (story) => {
      if (!story) return 0;

      // Interactive stories: ignore chapters, just top-level timestamps
      if (story.type === "interactive") {
        return (
          ts(story.updatedAt) ||
          ts(story.publishedAt) ||
          ts(story.createdAt)
        );
      }

      // Text / "normal" stories: consider chapters if present
      const topLevel =
        ts(story.updatedAt) ||
        ts(story.publishedAt) ||
        ts(story.createdAt);

      const chapters = story.chapters;
      if (!chapters) {
        return topLevel;
      }

      let maxChapter = 0;

      // If at some point you send chapters as an array, handle that too
      if (Array.isArray(chapters)) {
        const vals = chapters.map((ch) => ts(ch?.updatedAt));
        if (vals.length) maxChapter = Math.max(...vals);
      } else {
        // classic: object map of chapters
        const vals = Object.values(chapters).map((ch) => ts(ch?.updatedAt));
        if (vals.length) maxChapter = Math.max(...vals);
      }

      return Math.max(topLevel, maxChapter);
    };

    (async () => {
      setLoading(true);
      try {
        const [normalStories, interactiveStories] = await Promise.all([
          fetchUserStories().catch(() => []),
          fetchUserInteractiveStories().catch(() => []),
        ]);

        const merged = [
          ...(normalStories || []).map((s) => ({ ...s, type: s.type || "normal" })),
          ...(interactiveStories || []).map((s) => ({
            // backend already sets type:"interactive", but force it just in case
            ...s,
            type: s.type || "interactive",
          })),
        ];

        const sorted = merged.sort((a, b) => {
          const aLast = latestStoryActivity(a);
          const bLast = latestStoryActivity(b);

          // 1) primary: last activity (includes updatedAt/publishedAt/chapters)
          if (aLast !== bLast) return bLast - aLast;

          // 2) fallback: createdAt
          const aCr = ts(a.createdAt);
          const bCr = ts(b.createdAt);
          if (aCr !== bCr) return bCr - aCr;

          // 3) fallback: title
          return (a.title || "").localeCompare(b.title || "");
        });

        if (!cancelled) setStories(sorted);
      } catch (e) {
        console.error(e);
        if (!cancelled) setStories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "auto",
        padding: theme.spacing(2),
      }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          justifyContent: "flex-start",
          margin: "0 auto",
          width: "auto",
          maxWidth: "100%",
        }}
      >

        {/* New Interactive Story first */}
        <Grid2
          item
          xs={12}
          sm={6}
          md={4}
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        >
          <NewInteractiveStoryCard />
        </Grid2>
        {/* New Story Card as the first grid item */}
        <Grid2
          item
          xs={12}
          sm={6}
          md={4}
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        >
          <NewStoryCard />
        </Grid2>

        {loading
          ? // Render placeholder skeletons when loading
          Array.from(new Array(6)).map((_, index) => (
            <Grid2
              key={index}
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
            >
              <SkeletonStoryCard />
            </Grid2>
          ))
          : stories.length === 0
            ? // Show text when no stories exist
            (
              <Grid2 item xs={12}>
                <Typography align="center" color="textSecondary">
                  {t`No stories available. Start writing your first story!`}
                </Typography>
              </Grid2>
            )
            : // Render actual story cards
            stories.map((story) => (
              <Grid2
                key={story.id}
                item
                xs={12}
                sm={6}
                md={4}
                sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <StoryCard story={story} />
                </Box>
              </Grid2>
            ))}
      </Grid2>
    </Box>
  );
};

export default MyStoriesPage;
